import type { BaseServiceParams } from '@kiddocare-platform/portal-common/base';
import { useAppContext, type AppContextType } from '@kiddocare-platform/portal-common/contexts';
import type { Parent, Patient, Pediatrician, PediatricianPatient, Setting, SpecialistSpecialtyGroup } from '@kiddocare-platform/portal-common/core';
import { useLogger } from '@kiddocare-platform/portal-common/helpers';
import { PromptManager, ToasterManager } from '@kiddocare-platform/portal-common/ui';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { BillingHandlingService } from '../modules/billing/services/handling.service';
import { CertificationsConfigurationService } from '../modules/certifications/services/configuration.service';
import { ClinicalBookingService } from '../modules/clinical/services/booking.service';
import { ClinicalConfigurationService } from '../modules/clinical/services/configuration.service';
import { ClinicalExecutionService } from '../modules/clinical/services/execution.service';
import { CommunicationBroadcastService } from '../modules/communication/services/broadcast.service';
import { CommunicationCallsService } from '../modules/communication/services/call.service';
import { CommunicationChatsService } from '../modules/communication/services/chats.service';
import { CommunicationMessagingService } from '../modules/communication/services/messaging.service';
import { CommunicationNotificationService } from '../modules/communication/services/notification.service';
import { HubConfigurationService } from '../modules/hub/services/configuration.service';
import { CoreFlowService } from '../modules/core/services/flow.service';
import { CoreRegistryService } from '../modules/core/services/registry.service';
import { CoreTerritoryService } from '../modules/core/services/territory.service';
import { CoreUtilityService } from '../modules/core/services/utility.service';
import { HealthcareExecutionService } from '../modules/healthcare/services/execution.service';
import { PlatformLegalService } from '../modules/platform/services/legal.service';
import { RepositoryAvatarsService } from '../modules/repository/services/avatars.service';
import { RepositoryCertificatesService } from '../modules/repository/services/certificates.service';
import { RepositoryCommunicationService } from '../modules/repository/services/communication.service';
import { RepositoryDocumentsService } from '../modules/repository/services/documents.service';
import { RepositoryPrescriptionsService } from '../modules/repository/services/prescriptions.service';
import type { Permission, PermissionKey } from '@kiddocare-platform/portal-common/billing';
import type { Announcement } from '@kiddocare-platform/portal-common/communication';
import { CertificationsExecutionService } from '../modules/certifications/services/execution.service';
import { HubKiddyService } from '../modules/hub/services/kiddy.service';
import { HubExecutionService } from '../modules/hub/services/execution.service';
import { FamilyDiaryService } from '../modules/family/services/diary.service';
import { RepositoryFamilyService } from '../modules/repository/services/family.service';

/** Lista da `getPediatricianByPatient`: include `pediatricianPatients` per il figlio corrente. */
export type PediatricianWithPatientLink = Pediatrician & { pediatricianPatients?: PediatricianPatient[] };

export type MessageBlockSeverity = "WARNING" | "ERROR" | "NEUTRAL";

export interface MessageBlockFeature {
  label: string;
  type: MessageBlockSeverity;
}


export interface ParentContextType extends AppContextType {
  // noticesCount: number;
  // updateNoticesCount: () => Promise<void>;
  toPayInstancesCount: number;
  updateToPayInstancesCount: () => Promise<void>;
  isSettingsLoaded: boolean;
  isParentLoaded: boolean;
  isPatientsLoaded: boolean;
  parent: Parent | null;
  patients: Array<Patient> | null;
  selectedPatientIndex: number;
  selectedPatient: Patient | null;
  pediatricians: Array<PediatricianWithPatientLink> | undefined;
  selectedPediatricianIndex: number;
  keyboardOpen: boolean;
  setSelectedPediatricianIndex: React.Dispatch<React.SetStateAction<number>>;
  selectedPediatrician: PediatricianWithPatientLink | null;
  relationship: PediatricianPatient | null;
  settings: Array<Setting> | null;
  announcements: Array<Announcement> | undefined;
  specialistGroups: Array<SpecialistSpecialtyGroup> | undefined;
  fetchSpecialistGroups: () => Promise<Array<SpecialistSpecialtyGroup> | undefined>;
  setSettings: React.Dispatch<React.SetStateAction<Array<Setting> | null>>;
  setSelectedPatientIndex: React.Dispatch<React.SetStateAction<number>>;
  clearSettings: () => Promise<void>;
  fetchParent: () => Promise<Parent | undefined>;
  fetchSettings: () => Promise<void>;
  fetchPatients: () => Promise<Patient[] | undefined>;
  /** Ricarica i pediatri del `selectedPatient` corrente (es. dopo associazione). */
  getPediatricians: () => Promise<Array<PediatricianWithPatientLink> | undefined>;
  accountPermission: Permission | undefined;
  /** Abbonamento annullato (`status === "canceled"` dalla check subscription). */
  isSubscriptionCanceled: boolean;
  /** Processi/feature disabilitati dal pediatra (stesso payload di checkAllDisabledProcess: permessi, `AI`, …). */
  disabledProcesses: string[];
  fetchDisabledProcesses: () => Promise<void>;
  /**
   * Modalità consulto AI per il pediatra selezionato: se il backend include `AI` in `disabledProcesses`, non è attiva → `STANDARD`.
   */
  parentAiMode: "AI" | "STANDARD";
  /** Blocco servizi per relazione genitore–figlio / figlio–pediatra in attesa o rifiutata. */
  messageBlockFeature: MessageBlockFeature | undefined;
  /** Lettura storico (consultazioni, certificati, …) come in app. */
  canReadServices: boolean;
  /**
   * Nuove richieste (WRITE / CHATBOT): come su app mobile — consentite solo se non c’è
   * `messageBlockFeature` (storico / pending / … mostrano il banner e bloccano le azioni «nuove»).
   */
  canCreateServiceRequests: boolean;
  /** Ritorna `true` se il callback è stato eseguito, `false` se bloccato (toast già mostrato). */
  checkPermission: (permission: PermissionKey, callback: () => Promise<void> | void) => Promise<boolean>;
  handleKeyboardOpen: () => void;

  coreRegistryService: CoreRegistryService,
  coreTerritoryService: CoreTerritoryService,
  coreUtilityService: CoreUtilityService,
  coreFlowService: CoreFlowService,

  platformLegalService: PlatformLegalService,

  billingHandlingService: BillingHandlingService,

  communicationNotificationService: CommunicationNotificationService,
  communicationCallsService: CommunicationCallsService,
  communicationChatsService: CommunicationChatsService,
  communicationMessagingService: CommunicationMessagingService,
  communicationBroadcastService: CommunicationBroadcastService,

  hubConfigurationService: HubConfigurationService,
  hubKiddyService: HubKiddyService,
  hubExecutionService: HubExecutionService,

  repositoryAvatarsService: RepositoryAvatarsService,
  repositoryCertificatesService: RepositoryCertificatesService,
  repositoryDocumentsService: RepositoryDocumentsService,
  repositoryPrescriptionsService: RepositoryPrescriptionsService,
  repositoryCommunicationService: RepositoryCommunicationService,
  repositoryFamilyService: RepositoryFamilyService,

  certificationsConfigurationService: CertificationsConfigurationService,
  certificationsExecutionService: CertificationsExecutionService,

  healthcareExecutionService: HealthcareExecutionService,

  clinicalExecutionService: ClinicalExecutionService,
  clinicalConfigurationService: ClinicalConfigurationService,
  clinicalBookingService: ClinicalBookingService,

  familyDiaryService: FamilyDiaryService
}

// Crea il contesto con il tipo e un valore iniziale
export const ParentContext = createContext<ParentContextType | undefined>(undefined);

// Hook per accedere al contesto in modo più semplice
export const useParentContext = () => {
  const context = useContext(ParentContext);
  if (context === undefined) { throw new Error("ParentContext not found"); }
  return context;
};

export const ParentContextProvider: React.FC<{ children: ReactNode }> = (props) => {
  const { children } = props;
  const log = useLogger(ParentContextProvider.name);
  const { accessToken, user, handleLastActivity, device, ...restAppContext } = useAppContext();
  const [settings, setSettings] = useState<Array<Setting> | null>(null);
  const [parent, setParent] = useState<Parent | null>(null);
  const [patients, setPatients] = useState<Array<Patient> | null>(null);
  const [pediatricians, setPediatricians] = useState<Array<PediatricianWithPatientLink>>();
  const [accountPermission, setAccountPermission] = useState<Permission>();
  const [disabledProcesses, setDisabledProcesses] = useState<string[]>([]);
  const [isSettingsLoaded, setIsSettingsLoaded] = useState<boolean>(false);
  const [isParentLoaded, setIsParentLoaded] = useState<boolean>(false);
  const [isPatientsLoaded, setIsPatientsLoaded] = useState<boolean>(false);
  // const [noticesCount, setNoticesCount] = useState<number>(0);
  const [toPayInstancesCount, setToPayInstancesCount] = useState<number>(0);
  const [isAccountPermissionLoaded, setIsAccountPermissionLoaded] = useState<boolean>(false);
  const [selectedPatientIndex, setSelectedPatientIndex] = useState(0);
  const [selectedPediatricianIndex, setSelectedPediatricianIndex] = useState(0);
  const [announcements, setAnnouncements] = useState<Array<Announcement>>();
  const [specialistGroups, setSpecialistGroups] = useState<Array<SpecialistSpecialtyGroup>>();
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  // const pedDefaultsAppliedForRef = useRef<string>("");
  const { updateNoticesCount } = useAppContext();

  const selectedPatient = useMemo(() => {
    if (!patients || patients.length === 0) return null;
    return patients[selectedPatientIndex] || patients[0];
  }, [patients, selectedPatientIndex]);

  useEffect(() => {
    if (!pediatricians?.length) {
      setSelectedPediatricianIndex(0);
      return;
    }
    const ssnIdx = pediatricians.findIndex(
      (p) => p.pediatricianPatients?.[0]?.relationship === 'SSN',
    );
    if (ssnIdx >= 0) {
      setSelectedPediatricianIndex(ssnIdx);
      return;
    }
    const privateIdx = pediatricians.findIndex(
      (p) => p.pediatricianPatients?.[0]?.relationship === 'PRIVATE',
    );
    setSelectedPediatricianIndex(privateIdx >= 0 ? privateIdx : 0);
  }, [pediatricians]);

  // imposto l'offset del toaster in base alla posizione del bottom bar
  // in modo che non danno problema con la barra delle impostazioni nativa
  useEffect(() => {
    const currentOffsets = ToasterManager.attrs.offsets;
    const baseOffset = typeof currentOffsets === "string" ? currentOffsets : "1rem";

    ToasterManager.attrs.offsets = {
      top: baseOffset,
      left: baseOffset,
      right: baseOffset,
      bottom: device === "browser" ? baseOffset : "8rem"
    };
  }, [device]);

  // useEffect(() => {
  //   console.log("###pediatricians", pediatricians);
  //   if (!pediatricians?.length) {
  //     return;
  //   }
  //   setSelectedPediatricianIndex((i) => Math.min(i, Math.max(0, pediatricians.length - 1)));
  // }, [pediatricians]);

  const selectedPediatrician = useMemo(() => {
    if (!pediatricians?.length) {
      return null;
    }
    const i = selectedPediatricianIndex;
    if (i >= 0 && i < pediatricians.length) {
      return pediatricians[i]!;
    }
    return pediatricians[0] ?? null;
  }, [pediatricians, selectedPediatricianIndex]);

  const relationship = useMemo(() => {
    return selectedPediatrician?.pediatricianPatients?.[0] ?? null;
  }, [selectedPediatrician]);

  const parentPatientRelationship = useMemo(() => {
    return selectedPatient?.parentPatients?.[0];
  }, [selectedPatient]);

  const messageBlockFeature = useMemo((): MessageBlockFeature | undefined => {
    switch (parentPatientRelationship?.relationship) {
      // case undefined:
      //   return { label: "Seleziona un figlio per accedere ai servizi", type: "ERROR" };
      case 'PENDING':
        return { label: "Il pediatra selezionato sta valutando la tua richiesta di associazione.", type: "WARNING" };
      case 'REJECTED': {
        const note = parentPatientRelationship.data?.note;
        return {
          label: `Il pediatra selezionato ha rifiutato la tua richiesta di associazione.${note ? `\nMotivazione: ${note}` : ""}`,
          type: "ERROR",
        };
      }
      case 'HISTORICAL':
        return { label: "La relazione con questo pediatra è sospesa.", type: "NEUTRAL" };
    }
    switch (relationship?.relationship) {
      // case undefined:
      //   return { label: "Seleziona un pediatra per accedere ai servizi", type: "ERROR" };
      case 'UNDEFINED':
        return { label: "Il pediatra selezionato sta valutando la tua richiesta di associazione.", type: "WARNING" };
      case 'HISTORICAL':
        return {
          label:
            "La relazione con questo pediatra è sospesa.\nPuoi ancora vedere lo storico delle richieste, ma la funzione per crearne di nuove è disattivata.",
          type: "NEUTRAL",
        };
      case 'REJECTED': {
        const note = (relationship as { data?: { note?: string } } | null)?.data?.note;
        return {
          label: `Il pediatra selezionato ha rifiutato la tua richiesta di associazione.${note ? `\nMotivazione: ${note}` : ""}`,
          type: "ERROR",
        };
      }
    }
    return undefined;
  }, [parentPatientRelationship, relationship]);

  const canReadServices = useMemo(() => {
    const pr = parentPatientRelationship?.relationship;
    const pedRel = relationship?.relationship;
    if (!pr || !pedRel) {
      return false;
    }
    const ownerLike = pr === 'OWNER' || pr === 'OTHER';
    const pedOk =
      pedRel === 'SSN' ||
      pedRel === 'PRIVATE' ||
      pedRel === 'HISTORICAL';
    return ownerLike && pedOk;
  }, [parentPatientRelationship, relationship]);

  const canCreateServiceRequests = useMemo(
    () => Boolean(canReadServices && !messageBlockFeature),
    [canReadServices, messageBlockFeature],
  );

  // flag per sapere se ho già fatto la fetch dei settings
  const hasFetchedSettings = useRef(false);
  const services = useMemo(() => {
    if (!accessToken) {
      log.debug("$$$ : Access token non disponibile, servizi non inizializzati");
      return {} as ParentContextType;
    }

    log.debug("$$$ Inizializzo i servizi");
    const coreProps: BaseServiceParams = { accessToken, apiBaseUrl: import.meta.env.VITE_API_CORE_URL, onRequest: handleLastActivity };
    const repositoryProps: BaseServiceParams = { accessToken, apiBaseUrl: import.meta.env.VITE_API_REPOSITORY_URL, onRequest: handleLastActivity };
    const platformProps: BaseServiceParams = { accessToken, apiBaseUrl: import.meta.env.VITE_API_PLATFORM_URL, onRequest: handleLastActivity };
    const communicationProps: BaseServiceParams = { accessToken, apiBaseUrl: import.meta.env.VITE_API_COMMUNICATION_URL, onRequest: handleLastActivity };
    const hubProps: BaseServiceParams = { accessToken, apiBaseUrl: import.meta.env.VITE_API_HUB_URL, onRequest: handleLastActivity };
    const billingProps: BaseServiceParams = { accessToken, apiBaseUrl: import.meta.env.VITE_API_BILLING_URL, onRequest: handleLastActivity }
    const certificationProps: BaseServiceParams = { accessToken, apiBaseUrl: import.meta.env.VITE_API_CERTIFICATIONS_URL, onRequest: handleLastActivity };
    const healthcareExecutionProps: BaseServiceParams = { accessToken, apiBaseUrl: import.meta.env.VITE_API_HEALTHCARE_URL, onRequest: handleLastActivity };
    const clinicalProps: BaseServiceParams = { accessToken, apiBaseUrl: import.meta.env.VITE_API_CLINICAL_URL, onRequest: handleLastActivity };
    const familyProps: BaseServiceParams = { accessToken, apiBaseUrl: import.meta.env.VITE_API_FAMILY_URL, onRequest: handleLastActivity };

    return {
      coreRegistryService: new CoreRegistryService(coreProps),
      coreTerritoryService: new CoreTerritoryService(coreProps),
      coreUtilityService: new CoreUtilityService(coreProps),
      coreFlowService: new CoreFlowService(coreProps),

      platformLegalService: new PlatformLegalService(platformProps),

      billingHandlingService: new BillingHandlingService(billingProps),

      communicationNotificationService: new CommunicationNotificationService(communicationProps),
      communicationCallsService: new CommunicationCallsService(communicationProps),
      communicationChatsService: new CommunicationChatsService(communicationProps),
      communicationMessagingService: new CommunicationMessagingService(communicationProps),
      communicationBroadcastService: new CommunicationBroadcastService(communicationProps),

      hubConfigurationService: new HubConfigurationService(hubProps),
      hubKiddyService: new HubKiddyService(hubProps),
      hubExecutionService: new HubExecutionService(hubProps),

      repositoryAvatarsService: new RepositoryAvatarsService(repositoryProps),
      repositoryCertificatesService: new RepositoryCertificatesService(repositoryProps),
      repositoryDocumentsService: new RepositoryDocumentsService(repositoryProps),
      repositoryPrescriptionsService: new RepositoryPrescriptionsService(repositoryProps),
      repositoryCommunicationService: new RepositoryCommunicationService(repositoryProps),
      repositoryFamilyService: new RepositoryFamilyService(repositoryProps),

      certificationsConfigurationService: new CertificationsConfigurationService(certificationProps),
      certificationsExecutionService: new CertificationsExecutionService(certificationProps),

      healthcareExecutionService: new HealthcareExecutionService(healthcareExecutionProps),

      clinicalExecutionService: new ClinicalExecutionService(clinicalProps),
      clinicalConfigurationService: new ClinicalConfigurationService(clinicalProps),
      clinicalBookingService: new ClinicalBookingService(clinicalProps),

      familyDiaryService: new FamilyDiaryService(familyProps),
    }
  }, [accessToken]);

  const fetchSettings = useCallback(async () => {
    try {
      log.info("[#########################################] FETCH SETTINGS");
      const response = await services.coreRegistryService.CheckAccount();
      log.info("###Fetch Settings", response.data.length);
      const settingsSort = response.data?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setSettings(settingsSort);
    } catch (error: any) {
      log.error("###Errore Fetch Settings", error);
    } finally {
      setIsSettingsLoaded(true);
    }
  }, [accessToken, services]);

  const clearSettings = useCallback(async () => {
    setSettings(null);
  }, []);

  const fetchParent = useCallback(async () => {
    try {
      const response = await services.coreRegistryService.GetParentByAccountId();
      setParent(response.data);
      log.debug("PARENT", response.data.firstName);
      return response.data;
    } catch (error: any) {
      log.error("###Errore Fetch Pediatrician Info", error);
    } finally {
      setIsParentLoaded(true);
    }
  }, [services]);

  // const updateNoticesCount = useCallback(async () => {
  //   try {
  //     if (!services?.communicationNotificationService) return;

  //     const result = await services.communicationNotificationService.CountNotices();
  //     const count = result.data.count;

  //     setNoticesCount(count);
  //     BrowserHelper.updateFavicon(count);

  //     log.debug("###Notices Count", count);
  //   } catch (error) {
  //     log.error("###Errore Update Notices Count", error);
  //   }
  // }, [services]);

  const updateToPayInstancesCount = useCallback(async () => {
    try {
      if (!services?.billingHandlingService) return;

      const result = await services.billingHandlingService.GetToPayInstancesCount();
      const count = result.data?.count ?? 0;
      setToPayInstancesCount(count);
      log.debug("###To Pay Instances Count", count);
    } catch (error: any) {
      log.error("###Errore Update To Pay Instances Count", error);
      const code = error?.response?.data?.code;
      let message = "Si è verificato un errore durante il recupero del numero di pagamenti in attesa. Riprova più tardi.";
      if (typeof code === "string") {
        if (code.endsWith("AccountNotFound")) {
          message = "Account non trovato.";
        }
      }
      ToasterManager.error({ title: "Errore", description: message });
    }
  }, [services]);


  const fetchPatients = useCallback(async () => {
    try {
      const response = await services.coreRegistryService.PatientsSearch({ currentPage: 1, pageSize: 10, query: {} });
      setPatients(response.data);
      return response.data;
    } catch (error: any) {
      log.error("###Errore Fetch Pediatrician Info", error);
    } finally {
      setIsPatientsLoaded(true);
    }
  }, [services]);

  const fetchPediatricians = useCallback(async (idAccountPatient: bigint) => {
    try {
      const response = await services.coreRegistryService.GetPediatricianByPatient(idAccountPatient);
      return response.data;
    } catch (error: any) {
      log.error("###Errore Fetch Pediatricians", error);
    }
  }, [services]);

  const getPediatricians = useCallback(async () => {
    if (!selectedPatient?.account?.id) {
      setPediatricians(undefined);
      return;
    }
    const list = await fetchPediatricians(selectedPatient.account.id) as Array<PediatricianWithPatientLink>;
    setPediatricians(list ?? []);
    return list;
  }, [selectedPatient?.account?.id, fetchPediatricians]);

  const fetchDisabledProcesses = useCallback(async () => {
    if (!selectedPediatrician?.account?.id || !services.coreRegistryService) {
      setDisabledProcesses([]);
      return;
    }
    try {
      const response = await services.coreRegistryService.CheckAllDisableProcess(selectedPediatrician.account.id);
      setDisabledProcesses(response.data ?? []);
    } catch (error: unknown) {
      log.error("###Errore fetchDisabledProcesses", error);
      setDisabledProcesses([]);
    }
  }, [selectedPediatrician?.account?.id, services]);

  useEffect(() => {
    void fetchDisabledProcesses();
  }, [fetchDisabledProcesses]);

  const parentAiMode = useMemo<"AI" | "STANDARD">(
    () => (disabledProcesses.includes("AI") ? "STANDARD" : "AI"),
    [disabledProcesses],
  );

  const checkPermission = useCallback(
    async (key: PermissionKey, callback: () => Promise<void> | void): Promise<boolean> => {
      const writeLabels: Partial<Record<PermissionKey, string>> = {
        CERTIFICATIONS_WRITE: "certificato",
        CONSULTATIONS_WRITE: "consulto",
        CLINICAL_WRITE: "prenotazione",
        PRESCRIPTIONS_WRITE: "prescrizione",
        MESSAGINGS_WRITE: "messaggio",
        CHATBOT: "chatbot",
      };
      const readSectionLabels: Partial<Record<PermissionKey, string>> = {
        CERTIFICATIONS_READ: "Certificati",
        CONSULTATIONS_READ: "Parla con il pediatra",
        CLINICAL_READ: "Visite",
        PRESCRIPTIONS_READ: "Prescrizioni",
        MESSAGINGS_READ: "Messaggi dal pediatra",
      };
      if (!accountPermission?.permissions.includes(key)) {
        PromptManager.info({
          title: "Attenzione",
          content: "Non hai l'autorizzazione per accedere a questa sezione.",
        });
        return false;
      }
      const isNewRequestAction = key.endsWith("_WRITE");
      if (isNewRequestAction && !canCreateServiceRequests) {
        const content =
          messageBlockFeature?.label?.replace(/\n/g, " ").trim() ||
          "Non è possibile creare nuove richieste in questo momento.";
        PromptManager.info({
          title: "Attenzione",
          content,
        });
        return false;
      }
      if (disabledProcesses.includes(key)) {
        const isRead = key.endsWith("_READ");
        const content = isRead
          ? `Il pediatra ha momentaneamente disabilitato la sezione «${readSectionLabels[key] ?? "questa funzione"}».`
          : `Il pediatra ha momentaneamente disabilitato le nuove richieste di ${writeLabels[key] ?? "questa funzione"}.`;
        PromptManager.info({
          title: "Attenzione",
          content,
        });
        return false;
      }
      await callback();
      return true;
    },
    [accountPermission, canCreateServiceRequests, disabledProcesses, messageBlockFeature],
  );

  const fetchAccountPermission = async () => {
    try {
      const response = await services.billingHandlingService.Check();
      setAccountPermission(response.data);
      return response.data;
    } catch (error: any) {
      log.error("###Errore Fetch Account Permission", error);
    } finally {
      setIsAccountPermissionLoaded(true);
    }
  }

  // mi salvo il flag per sapere se l'abbonamento è stato cancellato
  const isSubscriptionCanceled = useMemo(
    () => isAccountPermissionLoaded && accountPermission?.status?.toLowerCase() === "canceled" || accountPermission?.status?.toLowerCase() === "past_due",
    [isAccountPermissionLoaded, accountPermission?.status],
  );

  useEffect(() => {
    log.debug("[useEffect] ", user);
    if (user && !hasFetchedSettings.current) {
      log.info("[useEffect] user autenticato, recupero settings");
      fetchSettings();
      hasFetchedSettings.current = true;
    }
    if (user && !isParentLoaded) {
      log.info("[useEffect] user autenticato, recupero pediatrician");
      fetchParent();
    }
    if (user && !isPatientsLoaded) {
      log.info("[useEffect] user autenticato, recupero pazienti");
      fetchPatients();
    }
    updateNoticesCount();
    updateToPayInstancesCount();
    if (user && !isAccountPermissionLoaded) {
      log.info("[useEffect] user autenticato, recupero subscriptions");
      fetchAccountPermission();
    }
  }, [user]);

  const fetchAnnouncements = useCallback(async () => {
    try {
      if (!services.communicationBroadcastService) {
        return
      };
      const response = await services.communicationBroadcastService.GetAnnouncements();
      setAnnouncements(response.data ?? []);
    } catch (error: any) {
      console.error(error);
      let message = "Errore durante il recupero delle comunicazioni. Riprova più tardi.";
      const code = error.response?.data?.code;
      if (typeof code === "string") {
        if (code.endsWith("AccountNotFound")) {
          message = "Account non trovato.";
        } else if (code.endsWith("AccountTypeNotFound")) {
          message = "Tipo account non disponibile.";
        }
      }
      PromptManager.error({ title: "Errore", content: message });
    }
  }, [services.communicationBroadcastService, setAnnouncements]);

  const handleKeyboardOpen = useCallback(() => {
    if (device !== "ios") {
      return
    };
    const onFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      const rect = target.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const isNearBottom = rect.top > viewportHeight * 0.5;
      if (isNearBottom) {
        setTimeout(() => setKeyboardOpen(true), 200);
      } else {
        setKeyboardOpen(false);
      }
    };
    const onBlur = () => {
      setTimeout(() => setKeyboardOpen(false), 200);
    };
    document.addEventListener("focusin", onFocus);
    document.addEventListener("focusout", onBlur);
    return () => {
      document.removeEventListener("focusin", onFocus);
      document.removeEventListener("focusout", onBlur);
      setKeyboardOpen(false);
    };
  }, [device, setKeyboardOpen]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const fetchSpecialistGroups = useCallback(async () => {
    try {
      if (specialistGroups !== undefined) {
        return specialistGroups;
      }
      if (!services.coreRegistryService) {
        return undefined;
      }
      const response = await services.coreRegistryService.SearchSpecialists();
      const groups = response.data ?? [];
      setSpecialistGroups(groups);
      return groups;
    } catch (error: any) {
      console.error(error);
      setSpecialistGroups([]);
      PromptManager.error({
        title: "Errore",
        content: "Errore durante il recupero degli specialisti. Riprova più tardi.",
      });
      return undefined;
    }
  }, [services.coreRegistryService, specialistGroups]);

  useEffect(() => {
    const selectPediatricians = async () => {
      const list = await getPediatricians();
      const ssnIdx = (list ?? [])?.findIndex(
        (p) => p.pediatricianPatients?.[0]?.relationship === 'SSN',
      );
      setSelectedPediatricianIndex(ssnIdx ?? 0);
    }
    selectPediatricians();
  }, [getPediatricians]);

  const value: ParentContextType = {
    accessToken,
    user,
    setSettings,
    setSelectedPatientIndex,
    handleLastActivity,
    isSettingsLoaded,
    isParentLoaded,
    isPatientsLoaded,
    settings,
    parent,
    patients,
    selectedPatientIndex,
    selectedPatient,
    pediatricians,
    selectedPediatricianIndex,
    announcements,
    specialistGroups,
    fetchSpecialistGroups,
    keyboardOpen,
    handleKeyboardOpen,
    setSelectedPediatricianIndex,
    selectedPediatrician,
    relationship,
    fetchParent,
    fetchPatients,
    getPediatricians,
    accountPermission,
    isSubscriptionCanceled,
    disabledProcesses,
    fetchDisabledProcesses,
    parentAiMode,
    messageBlockFeature,
    canReadServices,
    canCreateServiceRequests,
    checkPermission,
    device,
    ...restAppContext,
    ...services,
    // noticesCount,
    toPayInstancesCount,
    clearSettings,
    fetchSettings,
    // updateNoticesCount,
    updateToPayInstancesCount,
  };

  return (
    <ParentContext.Provider value={value}>
      {children}
    </ParentContext.Provider>
  );
}